import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Alert,
  CircularProgress,
  Chip,
  Button
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brandColors } from '../../theme/theme';
import { getUserCompany, getCompanyUsers } from '../../lib/services/enterpriseCompanyService';

/**
 * EnterpriseCompanyDashboard Page
 *
 * Company overview for Enterprise Admins
 */
const EnterpriseCompanyDashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const companyData = await getUserCompany(currentUser.uid);
      
      if (!companyData) {
        setError('No enterprise company found for this user');
        return;
      }
      
      setCompany(companyData);
      
      const companyUsers = await getCompanyUsers(companyData.id);
      setUsers(companyUsers);
    } catch (err) {
      console.error('Error fetching company data:', err);
      setError('Failed to load enterprise company information');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSeatUtilization = () => {
    if (!company) return 0;
    return (company.usedSeats / company.totalSeats) * 100;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !company) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Company not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Enterprise Company Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {company.companyName}
        </Typography>
      </Box>

      {/* Company Info Card */}
      <Card elevation={3} sx={{ mb: 4, border: `2px solid ${brandColors.primary}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 48, color: brandColors.primary }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {company.companyName}
              </Typography>
              <Chip
                label={company.subscriptionStatus || 'Active'}
                color="success"
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Subscription Expires
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(company.subscriptionExpiresAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Active Users
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {users.length} users
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Admins
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {company.enterpriseAdminUids?.length || 0}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Seat Usage */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Seat Utilization
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={getSeatUtilization()}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  bgcolor: `${brandColors.primary}20`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: getSeatUtilization() > 90 ? '#f44336' : 
                            getSeatUtilization() > 75 ? '#ff9800' : brandColors.primary
                  }
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right', fontWeight: 600 }}>
              {company.usedSeats} / {company.totalSeats} seats
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {(getSeatUtilization()).toFixed(1)}% utilized
          </Typography>
          
          {getSeatUtilization() > 90 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              You're running low on seats. Contact support to add more capacity.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/enterprise/company/users')}
              sx={{ bgcolor: brandColors.primary }}
            >
              Manage Users
            </Button>
            <Button
              variant="outlined"
              onClick={fetchData}
            >
              Refresh Data
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          To add more seats, change billing information, or upgrade your plan, please contact support at sales@greenlightanalytics.com
        </Typography>
      </Alert>
    </Container>
  );
};

export default EnterpriseCompanyDashboard;

